import { useEffect, useState } from 'react';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { Content, InfoCard, Link, Page } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  HomePageStarredEntities,
  HomePageRecentlyVisited,
  HomePageTopVisited,
} from '@backstage/plugin-home';
import Typography from '@mui/material/Typography';
import { HomePageSearchBar } from '@backstage/plugin-search';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BugReportIcon from '@mui/icons-material/BugReport';
import GitHubIcon from '@mui/icons-material/GitHub';
import CloudIcon from '@mui/icons-material/Cloud';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BuildIcon from '@mui/icons-material/Build';
import banner from '../../assets/devops-banner.jpg';
import { accents, monoFontFamily } from '../../theme';

const tools = [
  {
    url: 'https://console.cloud.google.com/',
    label: 'GCP Console',
    color: '#3B82F6',
    icon: <CloudIcon />,
  },
  {
    url: 'https://github.com/hulkag',
    label: 'GitHub',
    color: '#6E56CF',
    icon: <GitHubIcon />,
  },
  {
    url: 'https://grafana.mgmt.euw3.live.ds25.io/',
    label: 'Grafana',
    color: '#F46800',
    icon: <DashboardIcon />,
  },
  {
    url: 'https://digistore.atlassian.net/',
    label: 'Jira',
    color: '#2684FF',
    icon: <BugReportIcon />,
  },
  {
    url: '/docs',
    label: 'TechDocs',
    color: '#22A06B',
    icon: <DescriptionIcon />,
  },
  {
    url: 'https://hulkhulk.pagerduty.com/',
    label: 'PagerDuty',
    color: '#06AC38',
    icon: <NotificationsActiveIcon />,
  },
  {
    url: '/create',
    label: 'Scaffolder',
    color: '#E5484D',
    icon: <BuildIcon />,
  },
];

/**
 * The toolkit card, ours rather than plugin-home's HomePageToolkit so each
 * tool can get its own tinted bubble (like the banner's dinosaurs) without
 * styling another component's internal markup.
 */
const Toolkit = () => (
  <InfoCard title="Toolkit">
    <Box
      component="ul"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
        gap: 1,
        listStyle: 'none',
        m: 0,
        p: 0,
      }}
    >
      {tools.map(({ url, label, color, icon }) => (
        <li key={url}>
          <Link
            to={url}
            underline="none"
            style={{ display: 'block', borderRadius: 12 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                py: 1,
                borderRadius: 3,
                color: 'text.secondary',
                transition: 'background-color 120ms ease, transform 120ms ease',
                '&:hover': {
                  backgroundColor: alpha(color, 0.06),
                  color: 'text.primary',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  color,
                  backgroundColor: alpha(color, 0.12),
                  '& svg': { fontSize: 26 },
                }}
              >
                {icon}
              </Box>
              <Typography
                component="span"
                sx={{ fontSize: 12.5, fontWeight: 500, textAlign: 'center' }}
              >
                {label}
              </Typography>
            </Box>
          </Link>
        </li>
      ))}
    </Box>
  </InfoCard>
);

const greeting = (hour: number) => {
  if (hour < 5) return 'Up late';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

/** First name from the signed-in profile; empty for guests or on failure. */
const useFirstName = () => {
  const identityApi = useApi(identityApiRef);
  const [name, setName] = useState('');
  useEffect(() => {
    let cancelled = false;
    identityApi
      .getProfileInfo()
      .then(profile => {
        const first = profile.displayName?.trim().split(/\s+/)[0] ?? '';
        if (!cancelled && first.toLowerCase() !== 'guest') setName(first);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [identityApi]);
  return name;
};

const Hero = () => (
  <Box sx={{ position: 'relative', mb: { xs: 9, md: 7 } }}>
    <Box
      role="img"
      aria-label="Digistore24 DevOps: the team's plush dinosaurs on an alpine meadow"
      sx={{
        aspectRatio: '5 / 1',
        minHeight: 180,
        borderRadius: 5,
        backgroundImage: `url(${banner})`,
        backgroundSize: 'cover',
        backgroundPosition: 'left 60%',
        boxShadow: '0 12px 40px rgba(6,16,29,0.18)',
        // The banner is a daytime photo; take the edge off in dark mode
        filter: theme =>
          theme.palette.mode === 'dark' ? 'brightness(0.86)' : 'none',
      }}
    />
    {/* Search floats over the banner's bottom edge */}
    <Box
      sx={{
        position: 'absolute',
        left: '50%',
        bottom: 0,
        transform: 'translate(-50%, 50%)',
        width: { xs: 'calc(100% - 32px)', md: 'min(720px, 70%)' },
        zIndex: 1,
        // HomePageSearchBar is MUI v4, whose class names carry a generated
        // suffix (MuiInputBase-root-544), so match on the prefix
        '& [class*="MuiFormControl-root"]': { m: 0 },
        '& [class*="MuiInputBase-root"]': {
          backgroundColor: 'background.paper',
          borderRadius: '999px',
          height: 56,
          fontSize: 16,
          pl: 1,
          boxShadow: '0 10px 30px rgba(6,16,29,0.18)',
          border: theme => `1px solid ${theme.palette.divider}`,
        },
        '& [class*="MuiOutlinedInput-notchedOutline"]': {
          borderStyle: 'none',
        },
      }}
    >
      <HomePageSearchBar placeholder="Search catalog, docs, APIs…" />
    </Box>
  </Box>
);

const Greeting = () => {
  const firstName = useFirstName();
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="overline"
        component="p"
        sx={{
          color: theme =>
            theme.palette.mode === 'dark' ? accents.lime : 'primary.main',
          fontFamily: monoFontFamily,
        }}
      >
        Digistore24 / Developer Portal
      </Typography>
      <Typography variant="h2" component="h1" sx={{ mb: 0.5 }}>
        {greeting(new Date().getHours())}
        {firstName ? `, ${firstName}` : ''} 👋
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: 16 }}>
        Your services, docs and infrastructure, all in one place.
      </Typography>
    </Box>
  );
};

export const HomePage = () => {
  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
            <Hero />
            <Greeting />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <HomePageStarredEntities />
              </Grid>
              <Grid item xs={12} md={6}>
                <Toolkit />
              </Grid>
              <Grid item xs={12} md={6}>
                <HomePageRecentlyVisited />
              </Grid>
              <Grid item xs={12} md={6}>
                <HomePageTopVisited />
              </Grid>
            </Grid>
          </Box>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
